<script setup lang="ts">
  import { IconRestore } from '@gat-ship/icons'
  import type { Agency } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FieldGroup,
    FormField,
    FormSubmit,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderDescription,
    ModalSectionHeaderTitle,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { AgencyGroupPicker, CompanyPicker } from '@gat-ship/ref-data'
  import { agencyKey } from '@gat-ship/types/injection-keys'
  import { inject } from 'vue'

  const agencyProvider = inject(agencyKey)

  const isOpen = defineModel<boolean>('open', { default: false })
  const description = ref('')
  const agencyId = ref<string>('')

  const reactivateAgencySchema = z.object({
    companyCode: z.string().min(1, 'Please select a company'),
    parentGroupId: z.string().min(1, 'Please select an agency group')
  })
  type ReactivateAgencyFormValues = z.infer<typeof reactivateAgencySchema>

  const form = useForm({
    defaultValues: {
      companyCode: '',
      parentGroupId: ''
    } as ReactivateAgencyFormValues,
    validators: {
      onSubmit: reactivateAgencySchema
    },
    onSubmit: async ({ value }) => {
      const id = agencyId.value
      if (!id) return
      await agencyProvider?.commands.reactivate(id, {
        companyCode: value.companyCode,
        parentGroupId: value.parentGroupId
      })
      isOpen.value = false
    }
  })

  export interface ShowReactivateAgencyModalParams {
    agency: Agency
  }

  function showModal({ agency }: ShowReactivateAgencyModalParams) {
    agencyId.value = agency.id
    description.value = agency.name
    form.reset({ companyCode: '', parentGroupId: '' })
    isOpen.value = true
  }

  defineExpose({
    showModal
  })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconRestore class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Reactivate Agency
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <div class="flex flex-col gap-5">
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle> General Details </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription
              class="text-muted-foreground text-sm"
            >
              In order to reactivate this agency you must choose which company
              and agency group it belongs to.
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <FieldGroup>
            <FormField
              :form="form"
              name="companyCode"
              label="Company"
              hint="The legally bound company entity."
              required
            >
              <template #default="{ field, invalid }">
                <CompanyPicker
                  :id="field.name"
                  :model-value="field.state.value"
                  :aria-invalid="invalid"
                  :data-testid="testIds.common.select.company"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="parentGroupId"
              label="Agency Group"
              required
            >
              <template #default="{ field, invalid }">
                <AgencyGroupPicker
                  :id="field.name"
                  :model-value="field.state.value"
                  placeholder="Select Group"
                  :aria-invalid="invalid"
                  :data-testid="testIds.common.select.agencyGroup"
                  @update:model-value="field.handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle>
              What Happens During Reactivation?
            </ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription class="text-sm">
              The agency will be reactivated with it's current name, short name,
              country and contact info. Any other connections to this agency has
              been removed when it was deactivated. This means that you will
              have to add users, teams etc.
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
        </ModalSection>
      </div>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Reactivate" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
